import React, { useCallback, useMemo } from 'react';
import { parse } from 'date-fns';
import { DateCalendar } from '@mui/x-date-pickers-pro';
import { PickerSelectionState } from '@mui/x-date-pickers/internals/hooks/usePicker';
import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { getSelectedSingleDateFromStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FilterMenuSinglePanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuSinglePanel';
import { EVENT_STUDY_DATE_FORMAT_PATTERN } from 'libs/end-to-end/src/progress';
import { EVENT_STUDY_DATE_FORMATTER } from 'libs/end-to-end/src/progress';
import { formatDateStandard } from 'libs/end-to-end/src/dates';

export const SingleDateMenu = ({
  store,
  feature,
  databaseRevisionId,
  databaseId,
  organizationId,
  closeMenu,
  setPrimaryFilters,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  closeMenu: () => void;
  setPrimaryFilters: (params: {
    selections: PrimaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
}) => {
  const selectedDate = useMemo(() => {
    const selectedDateRaw = getSelectedSingleDateFromStore({
      store,
      feature,
      organizationId,
      databaseId,
      databaseRevisionId,
    });

    if (!selectedDateRaw) {
      return;
    }

    return parse(selectedDateRaw, EVENT_STUDY_DATE_FORMAT_PATTERN, new Date());
  }, [databaseId, databaseRevisionId, feature, organizationId, store]);

  const onSelectedDateChanged = useCallback(
    (
      selected: Date | null,
      selectionState: PickerSelectionState | undefined,
    ) => {
      if (!selected || selectionState !== 'finish') {
        return;
      }

      const primaryFiltersSelections: PrimaryFilterSelection[] = [
        {
          primaryFilterKey: 'singleDate',
          value: formatDateStandard({
            date: selected,
            formatter: EVENT_STUDY_DATE_FORMATTER,
          }),
        },
      ];

      setPrimaryFilters({
        selections: primaryFiltersSelections,
        shouldPreventAutoSubmit: true,
      });

      closeMenu();
    },
    [closeMenu, setPrimaryFilters],
  );

  return (
    <FilterMenuSinglePanel
      filterContent={
        <DateCalendar
          views={['year', 'month', 'day']}
          openTo="year"
          disableFuture={true}
          value={selectedDate}
          onChange={onSelectedDateChanged}
        />
      }
      bottomProps={'none'}
    />
  );
};
